import { LanguageSelect, Checkbox, Text, Button } from '@msc-b2c/shared-components';
import { Price } from './Price';
import styles from './passengers.module.scss';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import {
  AppDispatch,
  drawerPrice,
  getActiveSession,
  getAvailablePrice,
  getBeforePromotionPriceValue,
  getSmallKidAgeThreshold,
  isPromotionPriceAvailable,
  managePlurals,
  replacePlaceholders,
  RootState,
  roundPrice,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';

import { getCabinIdOfTheLoggedUser, getLanguageLabelFromCode } from '../../../utils/myAreaUtils';
import { getOrderedCabinsForPassengerDrawer } from '../../../utils/stateroomGenericUtils';
import {
  PassengerI,
  ShopExcursionDiscounDetailsI,
  ShopOBSPassengerI,
} from '@msc-b2c/interfaces/interfaces';
import { AgeCategoryEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { PassengersSkeleton } from './PassengersSkeleton';
import { getCabinByPassengerId } from '../../../utils/utils';
import { isOBSPriceBasisPerCabin } from '../../utils/DrawerContentUtilities';
import {
  filterOnlyEligiblePassengers,
  getLowestApplicablePrice,
  isPassengerAdultOrSenior,
  setAllPassengers,
} from '../../utils/PassengersUtilities';
import { extractLongDescription } from '../../utils/TooltipUtilities';
import { getLinkedMinorObs } from '../../utils/allInclusiveCmsHelpers';
import {
  getAlreadyBookedPassengerId,
  getBookedPassengerLanguage,
  getMergedBookedPassengerIdsForCombinedObs,
  isPassengerExcursionEligible,
  isPassengerRestrictedByItemAgeRequirements,
  hasObsCombinedRelatedPackages,
  hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner,
  passengersToRecord,
  isPriceTypeNotAllowed,
} from '../../utils/PostBookingResultsUtilities';
import HeadsUpContainer from './HeadsUpContainer';
import { DrawerPassengersProps } from '../../../../../../interfaces/src/lib/types/components/drawer/DrawerPassengersProps';
import { CartItem } from '@msc-b2c/interfaces/types';
import clsx from 'clsx';
import HeadsUpInfo, { HeadsUpMessagePriority } from './HeadsUpInfo';

function Passengers({
  isDrawerOpen,
  setPassengersToAdd,
  onCheckedPassengersChange,
  selectedLanguages,
  setSelectedLanguages,
  hidePassengersInCart = false,
  onAllPassengersInCartChange,
  isNoEligible = false,
  isRestrictedLanguageSelection = false,
  hasSelectedPassengers = false,
  isDining = false,
  diningConfirmLabel,
  diningConfirmLoadingLabel,
  diningConfirmLoadingLabelIcon,
  diningChildDiscountLabel,
  diningChildDiscountIndicatorLabel,
  diningButtonNext,
  hideDiningConfirmButton = false,
  preselectedPassengers,
  cabinIdFilter,
  showMainTitle = true,
  showCabinTitle = true,
  withRefundDivider = false,
  hideLongDescription = false,
  beforeLongDescription,
  headsUpMessage,
  disableCompletedDining = false,
  hideSelectAllCheckbox = false,
  showOnlyAddonEligiblePassengers = false,
  reservations = [],
  showGeneralHeadsUp = false,
  isDiningButtonLoading = false,
  setIsDiningButtonLoading,
}: Readonly<DrawerPassengersProps>) {
  const drawerAEMConfig = useSelector((state: RootState) => state.myAreaCommonConfig.drawerProps);
  const drawerExcursionAEMConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerExcursionProps?.drawerExcursionType
  );

  const {
    selectedMyAreaExcursionCard: myAreaExcursionCardData,
    selectedMyAreaOBSCard: myAreaOBSCardData,
    isLayerMyAreaExcursionOpen: isLayerExcursionOpen,
  } = useSelector((state: RootState) => state.myAreaDrawers);
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);
  const currentCard = isLayerExcursionOpen ? myAreaExcursionCardData : myAreaOBSCardData;
  const matchingCartItem = useMemo(() => {
    if (!currentCard) return undefined;
    const itemStartDate = 'startDate' in currentCard ? currentCard.startDate : undefined;

    const directItem = cartItems.find(
      (item: CartItem) =>
        (item.code === currentCard.code && item.itemType !== 'EXCURSION') ||
        (item.code === currentCard.code &&
          item.itemType === 'EXCURSION' &&
          itemStartDate !== undefined &&
          item.startDate === itemStartDate)
    );
    if (directItem) return directItem;

    const relatedItem = cartItems.find(
      (item: CartItem) => item.relatedItemCode === currentCard.code
    );
    if (relatedItem) return relatedItem;

    const baseCode = cartItems.find(
      (item: CartItem) => item.code === currentCard.code
    )?.relatedItemCode;
    return baseCode ? cartItems.find((item: CartItem) => item.code === baseCode) : undefined;
  }, [currentCard, cartItems]);
  const cartItemsForCurrent = useMemo(() => {
    if (!currentCard) return [];
    const itemStartDate = 'startDate' in currentCard ? currentCard.startDate : undefined;
    const directItems = cartItems.filter(
      (item) =>
        (item.code === currentCard.code && item.itemType !== 'EXCURSION') ||
        (item.code === currentCard.code &&
          item.itemType === 'EXCURSION' &&
          itemStartDate !== undefined &&
          item.startDate === itemStartDate)
    );
    const relatedItems = cartItems.filter((item) => item.relatedItemCode === currentCard.code);
    const baseCode = directItems[0]?.relatedItemCode;
    const baseItems = baseCode ? cartItems.filter((item) => item.code === baseCode) : [];
    return [...directItems, ...relatedItems, ...baseItems];
  }, [cartItems, currentCard]);
  const cartPassengerIds = useMemo(
    () => new Set(cartItemsForCurrent.flatMap((item) => item.passengers?.map((p) => p.id) ?? [])),
    [cartItemsForCurrent]
  );
  const cartCabinIds = useMemo(
    () => new Set(cartItemsForCurrent.flatMap((item) => item.cabins?.map((c) => c.id) ?? [])),
    [cartItemsForCurrent]
  );

  const promotionPriceEnabled = useSelector(
    (state: RootState) => state.myAreaCommonConfig.prePaidCardProps.promotionPriceEnabled
  );
  const { shopExcursion, shopOBS } = useSelector((state: RootState) => state.myCruise);
  const allOBS = useSelector((state: RootState) => state.myCruise.obs?.onboardServices);
  const allInclusiveCmsItems = useSelector(
    (state: RootState) => state.myCruise.allInclusiveCmsItems
  );
  const [lastSelectedLanguage, setLastSelectedLanguage] = useState('');

  const getPassengerAge = (passenger: PassengerI): number => passenger.age ?? 0;

  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const hasInfantsUnderThree = bookingData?.passengers?.some(
    (passenger) => (getPassengerAge(passenger) ?? 0) < getSmallKidAgeThreshold()
  );

  const languageList = myAreaExcursionCardData?.activityDetails?.guideLanguageCodes ?? [];
  const passengerData = bookingData?.passengers ?? [];
  const activeCruiseId = getActiveSession().cruiseId ?? '';
  const loggedUserCabinId = useMemo(
    () => getCabinIdOfTheLoggedUser(bookingData ?? undefined),
    [bookingData]
  );
  const cabinsForPassengerList = useMemo(
    () =>
      getOrderedCabinsForPassengerDrawer(
        bookingData,
        activeCruiseId || undefined,
        loggedUserCabinId
      ),
    [bookingData, activeCruiseId, loggedUserCabinId]
  );
  const excursionPassengerPrices = useMemo(
    () =>
      myAreaExcursionCardData?.code === shopExcursion?.code
        ? shopExcursion?.prices?.perPassenger ?? []
        : [],
    [myAreaExcursionCardData, shopExcursion]
  );

  const obsPassengerPrices = useMemo(
    () => (myAreaOBSCardData?.code === shopOBS?.code ? shopOBS?.prices?.perPassenger ?? [] : []),
    [myAreaOBSCardData, shopOBS]
  );
  const isPerCabinPriceBasis = isLayerExcursionOpen
    ? shopExcursion?.priceBasis === PriceBasisEnum.PER_CABIN
    : isOBSPriceBasisPerCabin(myAreaOBSCardData);
  const isAllInclusive = myAreaOBSCardData?.prepaidItems?.isAllInclusive; // put the service type from the correct API (getBooking or GetExcursion)
  const dispatch = useDispatch<AppDispatch>();
  const loggedUserId = getActiveSession().passengerId ?? '';

  const parkOBSType = myAreaOBSCardData?.type?.code === 'PARK';

  // Create a map for faster lookup of prices by passenger number
  const excursionPriceMap = new Map(
    excursionPassengerPrices?.map((pax) => [
      pax.number,
      {
        afterDiscount: pax.total?.afterDiscount,
        beforeDiscount: pax.total?.beforeDiscount,
        beforePromotionAndDiscount: pax.total?.beforePromotionAndDiscount,
      },
    ])
  );

  const obsPriceMap = new Map(
    obsPassengerPrices?.map((pax) => [
      pax.number,
      {
        afterDiscount: pax.total?.afterDiscount,
        beforeDiscount: pax.total?.beforeDiscount,
        beforePromotionAndDiscount: pax.total?.beforePromotionAndDiscount,
      },
    ])
  );

  /** Already-purchased multi-stateroom layout: match RefundPassengers (all cabins). */
  const useAllCabinsForBookedLookup = Boolean(cabinIdFilter);

  const alreadyBookedPaxID = useMemo(() => {
    if (!bookingData) {
      return [];
    }
    if (myAreaExcursionCardData) {
      return getAlreadyBookedPassengerId(
        myAreaExcursionCardData,
        bookingData,
        true,
        useAllCabinsForBookedLookup
      );
    }
    if (myAreaOBSCardData) {
      // Combined packages: include passengers booked on linked related codes.
      return getMergedBookedPassengerIdsForCombinedObs(
        myAreaOBSCardData,
        bookingData,
        allOBS,
        allInclusiveCmsItems,
        useAllCabinsForBookedLookup
      );
    }
    return [];
  }, [
    bookingData,
    myAreaExcursionCardData,
    myAreaOBSCardData,
    allOBS,
    allInclusiveCmsItems,
    useAllCabinsForBookedLookup,
  ]);
  const isDisabledCompletedDiningItem =
    isDining && disableCompletedDining && (alreadyBookedPaxID?.length || matchingCartItem);

  const passengersPerCabin = useMemo(() => {
    const result: Record<string, PassengerI[]> = {};
    cabinsForPassengerList?.forEach((cabin) => {
      const cabinPassengerIds = cabin?.passengers ?? [];

      if (!cabin?.id || !Array.isArray(cabinPassengerIds)) return;

      const cabinPassengerIdList = Array.isArray(cabinPassengerIds)
        ? cabinPassengerIds.map((pax) => (typeof pax === 'object' && pax !== null ? pax.id : pax))
        : [];

      const matchedPassengers = passengerData.filter(
        (passenger) => passenger?.id && cabinPassengerIdList.includes(passenger.id)
      );

      if (matchedPassengers.length > 0) {
        result[cabin.id] = matchedPassengers;
      }
    });
    return result;
  }, [cabinsForPassengerList, passengerData]);

  const activeUsersCabin = getCabinByPassengerId(cabinsForPassengerList, loggedUserId);

  const getEligiblePassengersForCabin = useCallback(
    (cabinId: string): PassengerI[] => {
      const passengers = passengersPerCabin[cabinId] ?? [];
      const ageReq = isLayerExcursionOpen
        ? shopExcursion?.ageRequirements
        : myAreaOBSCardData?.ageRequirements;

      return passengers.filter((passenger) => {
        if (!passenger.id) return false;
        if (alreadyBookedPaxID?.includes(passenger.id)) return false;

        const fullPassengerData = isLayerExcursionOpen
          ? excursionPassengerPrices?.find((px) => px.id === passenger.id)
          : obsPassengerPrices?.find((px) => px.id === passenger.id);
        const isApplicable = fullPassengerData?.isApplicable !== false;
        const isRestricted = isPassengerRestrictedByItemAgeRequirements(passenger, ageReq);
        const isParkNotApplicable = parkOBSType && fullPassengerData?.isApplicable === false;

        return isApplicable && !isRestricted && !isParkNotApplicable;
      });
    },
    [
      passengersPerCabin,
      isLayerExcursionOpen,
      shopExcursion?.ageRequirements,
      myAreaOBSCardData?.ageRequirements,
      alreadyBookedPaxID,
      excursionPassengerPrices,
      obsPassengerPrices,
      parkOBSType,
    ]
  );

  const getPassengerSelectionState = (
    selection?: Record<string, Record<string, boolean>> | null
  ): string => {
    if (!selection) {
      return '';
    }

    return Object.entries(selection)
      .sort(([firstCabinId], [secondCabinId]) => firstCabinId.localeCompare(secondCabinId))
      .map(([cabinId, passengers]) => {
        const selectedPassengerIds = Object.entries(passengers)
          .filter(([, isSelected]) => isSelected)
          .map(([passengerId]) => passengerId)
          .sort();

        return `${cabinId}:${selectedPassengerIds.join(',')}`;
      })
      .filter((entry) => !entry.endsWith(':'))
      .join('|');
  };

  // Helper function to get cart item passengers in the format needed for checkedPassengers
  const getCartItemPassengers = (
    cartItem: CartItem | undefined,
    passengersPerCabin: Record<string, PassengerI[]>
  ): Record<string, Record<string, boolean>> => {
    if (!cartItem?.passengers || cartItem.passengers.length === 0) {
      return {};
    }

    const cartPassengerIds = new Set(cartItem.passengers.map((p) => p.id));
    const cartCabinIds = new Set(cartItem.cabins?.map((c) => c.id) || []);

    const checkedState: Record<string, Record<string, boolean>> = {};
    Object.entries(passengersPerCabin).forEach(([cabinId, passengers]) => {
      checkedState[cabinId] = setAllPassengers(passengers, false);
    });

    // Check passengers that exist in the cart
    Object.entries(passengersPerCabin).forEach(([cabinId, passengers]) => {
      const shouldCheckCabin =
        cartItem.priceBasis === 'PER_CABIN' ? cartCabinIds.has(cabinId) : true;

      if (shouldCheckCabin) {
        passengers.forEach((passenger) => {
          if (passenger.id && cartPassengerIds.has(passenger.id)) {
            checkedState[cabinId][passenger.id] = true;
          }
        });
      }
    });

    return checkedState;
  };

  const [checkedPassengers, setCheckedPassengers] = useState<
    Record<string, Record<string, boolean>>
  >(() => {
    // If preselectedPassengers provided (e.g., from edit-back with preserve=true), use them
    if (preselectedPassengers) {
      return preselectedPassengers;
    }
    // Otherwise initialize as empty
    const state: Record<string, Record<string, boolean>> = {};
    Object.entries(passengersPerCabin).forEach(([cabinId, passengers]) => {
      state[cabinId] = setAllPassengers(passengers, false);
    });
    return state;
  });
  const lastDispatchedDrawerPriceRef = useRef('');
  const lastCartItemCodeRef = useRef<string | null>(null);
  const lastCartPassengersStrRef = useRef<string>('');
  const lastInitializedCardCodeRef = useRef<string | null>(null);
  const lastPassengersToAddSelectionRef = useRef<string>('');

  // Initialize checked passengers from cart when drawer opens
  useEffect(() => {
    const currentCardCode = currentCard?.code ?? null;

    if (!currentCard) {
      if (!isDrawerOpen) {
        resetState();
        lastInitializedCardCodeRef.current = null;
      }
      return;
    }

    // In dining edit flows, keep the incoming preselection as source of truth.
    if (isDrawerOpen && isDining && preselectedPassengers) {
      setCheckedPassengers((currentCheckedPassengers) =>
        getPassengerSelectionState(preselectedPassengers) ===
        getPassengerSelectionState(currentCheckedPassengers)
          ? currentCheckedPassengers
          : preselectedPassengers
      );
      lastInitializedCardCodeRef.current = currentCardCode;
      return;
    }

    if (isDrawerOpen) {
      if (matchingCartItem) {
        const cartCheckedPassengers = getCartItemPassengers(matchingCartItem, passengersPerCabin);
        const cartPassengersStr = getPassengerSelectionState(cartCheckedPassengers);
        const languageRecords = passengersToRecord(matchingCartItem.passengers);

        if (
          lastCartItemCodeRef.current !== currentCardCode ||
          lastCartPassengersStrRef.current !== cartPassengersStr
        ) {
          lastCartItemCodeRef.current = currentCardCode;
          lastCartPassengersStrRef.current = cartPassengersStr;
          setCheckedPassengers((currentCheckedPassengers) =>
            getPassengerSelectionState(currentCheckedPassengers) === cartPassengersStr
              ? currentCheckedPassengers
              : cartCheckedPassengers
          );

          if (setSelectedLanguages) {
            const currentLanguageSignature = JSON.stringify(selectedLanguages ?? {});
            const nextLanguageSignature = JSON.stringify(languageRecords ?? {});

            if (currentLanguageSignature !== nextLanguageSignature) {
              setSelectedLanguages(languageRecords);
            }
          }
        }
        lastInitializedCardCodeRef.current = currentCardCode;
      } else {
        const isCardChanged = lastInitializedCardCodeRef.current !== currentCardCode;
        // Reset only for a real card transition, not for transient cart recomputations.
        if (isCardChanged || lastCartItemCodeRef.current !== null) {
          resetState();
        }
        lastInitializedCardCodeRef.current = currentCardCode;
      }
    } else {
      if (lastInitializedCardCodeRef.current !== null || lastCartItemCodeRef.current !== null) {
        resetState();
      }
      lastInitializedCardCodeRef.current = null;
    }
  }, [
    isDrawerOpen,
    currentCard?.code,
    cartItems,
    passengersPerCabin,
    hidePassengersInCart,
    isDining,
    preselectedPassengers,
  ]);

  useEffect(() => {
    if (onCheckedPassengersChange && isDrawerOpen) {
      onCheckedPassengersChange(checkedPassengers, selectedLanguages);
    }
  }, [checkedPassengers, onCheckedPassengersChange, isDrawerOpen, selectedLanguages]);

  const remainingEligiblePassengerIds = useMemo(() => {
    if (!hidePassengersInCart) return [];
    const ageReq = isLayerExcursionOpen
      ? shopExcursion?.ageRequirements
      : myAreaOBSCardData?.ageRequirements;
    const shopPassengerPrices = isLayerExcursionOpen
      ? shopExcursion?.prices?.perPassenger
      : obsPassengerPrices;
    const passengersGroupsToCheck =
      cabinIdFilter && passengersPerCabin[cabinIdFilter]
        ? [passengersPerCabin[cabinIdFilter]]
        : activeUsersCabin && passengersPerCabin[activeUsersCabin.id]
        ? [passengersPerCabin[activeUsersCabin.id]]
        : Object.values(passengersPerCabin);

    return passengersGroupsToCheck
      .flatMap((passengers) =>
        passengers.filter((passenger) => {
          if (!passenger.id) return false;
          if (alreadyBookedPaxID?.includes(passenger.id)) return false;
          if (cartPassengerIds.has(passenger.id)) return false;

          if (isPassengerRestrictedByItemAgeRequirements(passenger, ageReq)) {
            return false;
          }

          const passengerShopData = shopPassengerPrices?.find((px) => px.id === passenger.id);
          const isApplicable = passengerShopData ? passengerShopData.isApplicable !== false : true;

          const isApplicableForPark =
            !isLayerExcursionOpen &&
            myAreaOBSCardData?.type?.code === 'PARK' &&
            passengerShopData?.isApplicable === false;

          return isApplicable && !isApplicableForPark;
        })
      )
      .map((passenger) => passenger.id);
  }, [
    hidePassengersInCart,
    passengersPerCabin,
    alreadyBookedPaxID,
    cartPassengerIds,
    isLayerExcursionOpen,
    shopExcursion?.prices?.perPassenger,
    obsPassengerPrices,
    shopExcursion?.ageRequirements,
    myAreaOBSCardData?.ageRequirements,
    myAreaOBSCardData?.type?.code,
    cabinIdFilter,
  ]);

  const remainingEligibleCabinIds = useMemo(() => {
    if (!hidePassengersInCart) return [];
    const ageReq = isLayerExcursionOpen
      ? shopExcursion?.ageRequirements
      : myAreaOBSCardData?.ageRequirements;
    const shopPassengerPrices = isLayerExcursionOpen
      ? shopExcursion?.prices?.perPassenger
      : obsPassengerPrices;

    const cabinsToCheck: [string, PassengerI[]][] =
      cabinIdFilter && passengersPerCabin[cabinIdFilter]
        ? [[cabinIdFilter, passengersPerCabin[cabinIdFilter]]]
        : activeUsersCabin && passengersPerCabin[activeUsersCabin.id]
        ? [[activeUsersCabin.id, passengersPerCabin[activeUsersCabin.id]]]
        : Object.entries(passengersPerCabin);

    return cabinsToCheck
      .filter(([cabinId, passengers]) => {
        if (cartCabinIds.has(cabinId)) return false;
        return passengers.some((passenger) => {
          if (!passenger.id) return false;
          if (alreadyBookedPaxID?.includes(passenger.id)) return false;

          if (isPassengerRestrictedByItemAgeRequirements(passenger, ageReq)) {
            return false;
          }

          const passengerShopData = shopPassengerPrices?.find((px) => px.id === passenger.id);
          const isApplicable = passengerShopData ? passengerShopData.isApplicable !== false : true;

          const isApplicableForPark =
            !isLayerExcursionOpen &&
            myAreaOBSCardData?.type?.code === 'PARK' &&
            passengerShopData?.isApplicable === false;

          return isApplicable && !isApplicableForPark;
        });
      })
      .map(([cabinId]) => cabinId);
  }, [
    hidePassengersInCart,
    passengersPerCabin,
    alreadyBookedPaxID,
    cartCabinIds,
    isLayerExcursionOpen,
    shopExcursion?.prices?.perPassenger,
    obsPassengerPrices,
    shopExcursion?.ageRequirements,
    myAreaOBSCardData?.ageRequirements,
    myAreaOBSCardData?.type?.code,
    cabinIdFilter,
  ]);

  const inactiveCabinsBookedPaxIds = useMemo(() => {
    if (useAllCabinsForBookedLookup) {
      return [];
    }
    return (
      bookingData && myAreaExcursionCardData
        ? getAlreadyBookedPassengerId(myAreaExcursionCardData, bookingData, true, true)
        : bookingData && myAreaOBSCardData
        ? getAlreadyBookedPassengerId(myAreaOBSCardData, bookingData, false, true)
        : []
    ).filter((passengerId) => !alreadyBookedPaxID.includes(passengerId));
  }, [
    bookingData,
    myAreaExcursionCardData,
    myAreaOBSCardData,
    alreadyBookedPaxID,
    useAllCabinsForBookedLookup,
  ]);

  const hasCabinPassengers = cabinIdFilter
    ? (passengersPerCabin[cabinIdFilter] ?? []).length > 0
    : activeUsersCabin
    ? (passengersPerCabin[activeUsersCabin.id] ?? []).length > 0
    : Object.values(passengersPerCabin).some((passengers) => passengers.length > 0);

  const allPassengersInCart =
    hidePassengersInCart &&
    !isNoEligible &&
    !!matchingCartItem &&
    hasCabinPassengers &&
    (matchingCartItem.priceBasis === PriceBasisEnum.PER_CABIN
      ? cartCabinIds.size > 0 && remainingEligibleCabinIds.length === 0
      : cartPassengerIds.size > 0 && remainingEligiblePassengerIds.length === 0);

  const currentDrawerCheckedPassengers = useMemo(() => {
    if (!hidePassengersInCart || cartPassengerIds.size === 0) {
      return checkedPassengers;
    }

    return Object.fromEntries(
      Object.entries(checkedPassengers).map(([cabinId, cabinPassengers]) => [
        cabinId,
        Object.fromEntries(
          Object.entries(cabinPassengers).map(([passengerId, isChecked]) => [
            passengerId,
            cartPassengerIds.has(passengerId) ? false : isChecked,
          ])
        ),
      ])
    );
  }, [checkedPassengers, hidePassengersInCart, cartPassengerIds]);

  const isCombinedObsPackage = hasObsCombinedRelatedPackages(myAreaOBSCardData);

  // When isDining is true, detect child/junior passengers whose price is discounted
  // relative to the adult price. Used to show an asterisk and a discount label.
  const diningDiscountedMinorIds = useMemo(() => {
    if (!isDining) return new Set<string>();

    const prices = isLayerExcursionOpen ? excursionPassengerPrices : obsPassengerPrices;
    if (!prices || prices.length === 0) return new Set<string>();

    const adultPriceEntry = prices.find(
      (p) => p.isApplicable && p.ageCategory === AgeCategoryEnum.ADULT
    );
    const adultPriceBeforeDiscount =
      adultPriceEntry?.total?.beforePromotionAndDiscount || adultPriceEntry?.total?.beforeDiscount;
    if (!adultPriceBeforeDiscount) return new Set<string>();

    const adultPrice = Number.parseFloat(adultPriceBeforeDiscount);
    if (Number.isNaN(adultPrice) || adultPrice <= 0) return new Set<string>();

    const discountedIds = new Set<string>();
    prices.forEach((p) => {
      if (
        p.isApplicable &&
        p.id &&
        (p.ageCategory === AgeCategoryEnum.CHILD || p.ageCategory === AgeCategoryEnum.JUNIOR)
      ) {
        const minorPrice = Number.parseFloat(
          (p.total?.beforePromotionAndDiscount || p.total?.beforeDiscount) ?? '0.00'
        );
        if (!Number.isNaN(minorPrice) && minorPrice < adultPrice) {
          discountedIds.add(p.id);
        }
      }
    });

    return discountedIds;
  }, [isDining, isLayerExcursionOpen, excursionPassengerPrices, obsPassengerPrices]);

  const showDiningChildDiscountLabel = diningDiscountedMinorIds.size > 0;

  // For an adult ALLINC card with a linked minor package, the minor's prices carry their own
  // promotion window. Resolve it so minor passengers use the minor card's promotion dates
  // (mirrors the card pricing in getPostBookingCardPrices) instead of the adult card's.
  const linkedMinorObsCard = useMemo(
    () => getLinkedMinorObs(myAreaOBSCardData, allOBS, allInclusiveCmsItems),
    [myAreaOBSCardData, allOBS, allInclusiveCmsItems]
  );

  const hasSomeNotApplicablePassengers = useMemo(
    () =>
      hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner(
        obsPassengerPrices,
        isCombinedObsPackage
      ),
    [obsPassengerPrices, isCombinedObsPackage]
  );

  const shouldShowHeadsUpContainer =
    showGeneralHeadsUp ||
    (!isLayerExcursionOpen &&
      hidePassengersInCart &&
      !showOnlyAddonEligiblePassengers &&
      (allPassengersInCart || isNoEligible || hasSomeNotApplicablePassengers));

  const isMultiCabin = Object.entries(passengersPerCabin).length > 1;
  /** Already-purchased grouped drawer: one Passengers instance per stateroom — keep subtitle inside each block. */
  const showPerCabinPassengersHeading = isMultiCabin && Boolean(cabinIdFilter);

  const onAllPassengersInCartChangeRef = useRef(onAllPassengersInCartChange);
  const lastReportedAllPassengersInCartRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    onAllPassengersInCartChangeRef.current = onAllPassengersInCartChange;
  }, [onAllPassengersInCartChange]);

  useEffect(() => {
    if (!hidePassengersInCart) {
      lastReportedAllPassengersInCartRef.current = undefined;
    }
  }, [hidePassengersInCart]);

  useEffect(() => {
    if (!hidePassengersInCart || !onAllPassengersInCartChangeRef.current) {
      return;
    }
    if (lastReportedAllPassengersInCartRef.current === allPassengersInCart) {
      return;
    }
    lastReportedAllPassengersInCartRef.current = allPassengersInCart;
    onAllPassengersInCartChangeRef.current(allPassengersInCart);
  }, [hidePassengersInCart, allPassengersInCart]);

  useEffect(() => {
    const checkedSelectionSignature = getPassengerSelectionState(checkedPassengers);

    if (lastPassengersToAddSelectionRef.current === checkedSelectionSignature) {
      return;
    }

    lastPassengersToAddSelectionRef.current = checkedSelectionSignature;
    setPassengersToAdd(checkedPassengers);
  }, [checkedPassengers, setPassengersToAdd]);

  useEffect(() => {
    if (!alreadyBookedPaxID?.length || !isDrawerOpen || !myAreaExcursionCardData || !bookingData) {
      return;
    }

    const language = getBookedPassengerLanguage(
      myAreaExcursionCardData.code,
      bookingData,
      myAreaExcursionCardData.startDate
    );
    setLastSelectedLanguage(language);
  }, [alreadyBookedPaxID, isDrawerOpen, myAreaExcursionCardData]);

  useEffect(() => {
    const isBatchLanguageSelectionAvailable =
      selectedLanguages && lastSelectedLanguage && setSelectedLanguages && activeUsersCabin;

    if (!isBatchLanguageSelectionAvailable) {
      return;
    }

    const eligiblePassengersId = Object.entries(checkedPassengers[activeUsersCabin.id] ?? {})
      .map(([passengerId]) =>
        bookingData?.passengers?.find((bookingPassenger) => passengerId === bookingPassenger.id)
      )
      .filter(
        (passenger) =>
          passenger?.id &&
          isPassengerExcursionEligible(
            myAreaExcursionCardData,
            shopExcursion,
            bookingData,
            passenger
          )
      )
      .map((passenger) => passenger?.id)
      .filter((passengerId): passengerId is string => Boolean(passengerId));

    const missingPassengerIds = eligiblePassengersId.filter(
      (passengerId) => !selectedLanguages[passengerId]?.length
    );

    if (!missingPassengerIds.length) {
      return;
    }

    const newState = Object.fromEntries(
      missingPassengerIds.map((passengerId) => [passengerId, lastSelectedLanguage])
    );
    const nextSelectedLanguages = { ...selectedLanguages, ...newState };

    if (JSON.stringify(nextSelectedLanguages) === JSON.stringify(selectedLanguages)) {
      return;
    }

    setSelectedLanguages(nextSelectedLanguages);
  }, [
    selectedLanguages,
    lastSelectedLanguage,
    setSelectedLanguages,
    activeUsersCabin,
    checkedPassengers,
    bookingData,
    myAreaExcursionCardData,
    shopExcursion,
  ]);

  function onlyMinorsEligible(passengers: PassengerI[], ageReq: any, alreadyBookedPaxID: string[]) {
    // This checks if all passengers are minors or not eligible due to age restrictions
    return passengers.every((p) => {
      if (!p.id) return true;
      const age = p.age ?? 0;
      const restricted =
        (ageReq?.minimum !== undefined && age < ageReq.minimum) ||
        (ageReq?.maximum !== undefined && age > ageReq.maximum);
      const alreadyBooked = alreadyBookedPaxID?.includes(p.id);
      // Ignore not eligible passengers
      if (restricted || alreadyBooked) return true;
      // If not adult/senior, it's a minor
      return !isPassengerAdultOrSenior(p);
    });
  }

  function isAllCabinChecked(cabinId: string): boolean {
    const currentCabinPassengers = checkedPassengers[cabinId];
    const passengers = passengersPerCabin[cabinId];
    if (!currentCabinPassengers || !passengers) return false;

    const infantAgeThreshold = getSmallKidAgeThreshold();
    const ageReq = shopExcursion?.ageRequirements;
    let hasEligiblePassenger = false;

    for (const passenger of passengers) {
      const id = passenger.id;
      if (!id) continue;

      const age = getPassengerAge(passenger) ?? 0;
      const isUnderAgeThreshold = age < infantAgeThreshold;

      const isRestrictedByAge =
        (ageReq?.minimum !== undefined && age < ageReq.minimum) ||
        (ageReq?.maximum !== undefined && age > ageReq.maximum);

      const isAlreadyBooked = alreadyBookedPaxID?.includes(id);

      const isParkTypeNotApplicable =
        parkOBSType &&
        !(isLayerExcursionOpen
          ? excursionPassengerPrices?.find((passenger) => passenger.id === id)?.isApplicable
          : obsPassengerPrices?.find((passenger) => passenger.id === id)?.isApplicable);

      const isPassengerEligible = isLayerExcursionOpen
        ? excursionPassengerPrices?.find((passenger) => passenger.id === id)?.isApplicable
        : obsPassengerPrices?.find((passenger) => passenger.id === id)?.isApplicable;

      const manualDeselected = manualUnderThreeDeselects?.[cabinId]?.[id] === true;

      const isEligible =
        !isRestrictedByAge &&
        !isAlreadyBooked &&
        !isParkTypeNotApplicable &&
        isPassengerEligible &&
        (!isUnderAgeThreshold || isLayerExcursionOpen || !manualDeselected);

      if (!isEligible) continue;

      hasEligiblePassenger = true;

      // Eligible but not checked
      if (!currentCabinPassengers[id]) {
        return false;
      }
    }
    return hasEligiblePassenger;
  }

  const {
    formState: { errors },
    clearErrors,
    control,
    reset,
  } = useForm({
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const [manualUnderThreeDeselects, setManualUnderThreeDeselects] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const resetState = () => {
    const nextResetState: Record<string, Record<string, boolean>> = {};
    Object.entries(passengersPerCabin).forEach(([cabinId, passengers]) => {
      nextResetState[cabinId] = setAllPassengers(passengers, false);
    });

    const nextResetSelectionSignature = getPassengerSelectionState(nextResetState);
    setCheckedPassengers((currentCheckedPassengers) =>
      getPassengerSelectionState(currentCheckedPassengers) === nextResetSelectionSignature
        ? currentCheckedPassengers
        : nextResetState
    );

    setSelectedLanguages?.({});
    setLastSelectedLanguage?.('');
    lastCartItemCodeRef.current = null;
    lastCartPassengersStrRef.current = '';
    reset({ value: '' });
  };

  const handleCabinCheckboxChange = (cabinId: string, passenger: PassengerI) => {
    if (!passenger.id) return;
    const passengerId = passenger.id;
    if (hidePassengersInCart && cartPassengerIds.has(passengerId)) return;
    const age = getPassengerAge(passenger) ?? 0;
    const isUnderAgeThreshold = age < getSmallKidAgeThreshold();

    const isPerCabin = isPerCabinPriceBasis;
    const isPark = parkOBSType;

    const passengersInCabin = passengersPerCabin[cabinId] || [];
    const ageReq = shopExcursion?.ageRequirements;

    const onlyMinors = onlyMinorsEligible(passengersInCabin, ageReq, alreadyBookedPaxID);

    if (isAllInclusive) {
      toggleAllPassengers();
      return;
    }

    if (isPark) {
      // Select only the current passenger
      const newCabinCheckedForPark: Record<string, boolean> = {};
      newCabinCheckedForPark[passengerId] = true;

      setCheckedPassengers((prev) => ({
        ...prev,
        [cabinId]: newCabinCheckedForPark,
      }));
      return;
    }

    if (isPerCabin) {
      const passengersInCabinList = passengersPerCabin[cabinId] || [];
      const eligiblePassengers = getEligiblePassengersForCabin(cabinId);
      const currentCabinChecks = checkedPassengers[cabinId] ?? {};

      const allEligibleChecked =
        eligiblePassengers.length > 0 &&
        eligiblePassengers.every((p) => p.id && currentCabinChecks[p.id]);

      const shouldSelect = !allEligibleChecked;
      const newCabinChecked: Record<string, boolean> = {};

      passengersInCabinList.forEach((p) => {
        if (!p.id) return;
        const isEligible = eligiblePassengers.some((ep) => ep.id === p.id);
        newCabinChecked[p.id] = isEligible && shouldSelect;
      });

      setCheckedPassengers((prev) => ({
        ...prev,
        [cabinId]: newCabinChecked,
      }));
      return;
    }

    //Under-3 logic, manual deselection, excursion API checks)

    setCheckedPassengers((prevChecked) => {
      const currentCabinChecked = prevChecked[cabinId] || {};
      const wasChecked = currentCabinChecked[passengerId] ?? false;
      const isSelecting = !wasChecked;

      const passengersInCabin = passengersPerCabin[cabinId] || [];
      const ageReq = shopExcursion?.ageRequirements;

      if (isSelecting && (!isLayerExcursionOpen || isPassengerAdultOrSenior(passenger))) {
        const allowedPassengers = passengersInCabin.filter((p) => {
          if (!p.id) return false;
          const pAge = getPassengerAge(p) ?? 0;
          const restricted =
            (ageReq?.minimum !== undefined && pAge < ageReq.minimum) ||
            (ageReq?.maximum !== undefined && pAge > ageReq.maximum);
          const alreadyBooked = alreadyBookedPaxID?.includes(p.id) ?? false;
          const isApplicable = isLayerExcursionOpen
            ? excursionPassengerPrices?.find((px) => px.id === p.id)?.isApplicable
            : obsPassengerPrices?.find((px) => px.id === p.id)?.isApplicable;
          const notApplicable =
            isPark &&
            !(isLayerExcursionOpen
              ? excursionPassengerPrices?.find((px) => px.id === p.id)?.isApplicable
              : obsPassengerPrices?.find((px) => px.id === p.id)?.isApplicable);
          return !restricted && !alreadyBooked && !notApplicable && isApplicable !== false;
        });

        const adults = allowedPassengers.filter((p) => isPassengerAdultOrSenior(p));

        const toggledCabinChecked = {
          ...currentCabinChecked,
          [passengerId]: true,
        };

        const selectedAdultCount = adults.reduce(
          (count, adult) => (toggledCabinChecked[adult.id] ? count + 1 : count),
          0
        );

        if (hasInfantsUnderThree && selectedAdultCount === adults.length && adults.length > 0) {
          const allNonInfantsUnderThreeSelected = allowedPassengers.every((p) => {
            if (!p.id) return true;
            const isInfantUnderThree = getPassengerAge(p) < getSmallKidAgeThreshold();
            return isInfantUnderThree || toggledCabinChecked[p.id];
          });

          if (!isLayerExcursionOpen && !allNonInfantsUnderThreeSelected) {
            return {
              ...prevChecked,
              [cabinId]: toggledCabinChecked,
            };
          }

          const newCabinChecked = { ...toggledCabinChecked };
          allowedPassengers.forEach((p) => {
            if (!p.id) return;
            if (isLayerExcursionOpen || getPassengerAge(p) < getSmallKidAgeThreshold()) {
              newCabinChecked[p.id] = true;
            }
          });

          setManualUnderThreeDeselects((prev) => ({
            ...prev,
            [cabinId]: {},
          }));

          return {
            ...prevChecked,
            [cabinId]: newCabinChecked,
          };
        } else {
          return {
            ...prevChecked,
            [cabinId]: toggledCabinChecked,
          };
        }
      }

      // If deselecting or not an adult
      const updatedCabinChecked = {
        ...currentCabinChecked,
        [passengerId]: !wasChecked,
      };

      const adults = passengersInCabin.filter((p) => isPassengerAdultOrSenior(p) && p.id);
      const selectedAdultCount = adults.reduce(
        (count, adult) => (updatedCabinChecked[adult.id] ? count + 1 : count),
        0
      );

      const adultsAlreadyBooked = alreadyBookedPaxID.some((id) =>
        adults.some((adult) => adult.id === id)
      );

      //De-select under-age passengers if no adults selected (and not already booked)
      if (selectedAdultCount === 0 && !adultsAlreadyBooked && !onlyMinors && isLayerExcursionOpen) {
        passengersInCabin
          .filter(
            (p) =>
              (p.ageCategory === AgeCategoryEnum.CHILD ||
                p.ageCategory === AgeCategoryEnum.INFANT ||
                p.ageCategory === AgeCategoryEnum.JUNIOR) &&
              p.id
          )
          .forEach((underAge) => {
            updatedCabinChecked[underAge.id] = false;
          });
      }

      if (isUnderAgeThreshold) {
        const updatedManual = {
          ...manualUnderThreeDeselects,
          [cabinId]: {
            ...manualUnderThreeDeselects[cabinId],
            [passengerId]: wasChecked,
          },
        };
        setManualUnderThreeDeselects(updatedManual);
      }

      return {
        ...prevChecked,
        [cabinId]: updatedCabinChecked,
      };
    });
  };

  function handleSelectAllCabin(cabinId: string) {
    const allChecked = isAllCabinChecked(cabinId);

    const passengers = isPerCabinPriceBasis
      ? getEligiblePassengersForCabin(cabinId)
      : filterOnlyEligiblePassengers(
          passengersPerCabin[cabinId],
          isLayerExcursionOpen ? shopExcursion : shopOBS
        );
    const infantAgeThreshold = getSmallKidAgeThreshold();
    const ageReq = shopExcursion?.ageRequirements;

    const updated: Record<string, boolean> = {};
    const newAutoSelected: Record<string, boolean> = {};

    const manuallyDeselectedMap = { ...manualUnderThreeDeselects };
    if (!allChecked) {
      // Clear manual deselections for this cabin (in-memory AND later with set)
      manuallyDeselectedMap[cabinId] = {};
      setManualUnderThreeDeselects(manuallyDeselectedMap);
    }

    passengers.forEach((passenger) => {
      const age = getPassengerAge(passenger);
      const isUnderAgeThreshold = age !== undefined && age < infantAgeThreshold;

      const isRestricted =
        (ageReq?.minimum !== undefined && age < ageReq.minimum) ||
        (ageReq?.maximum !== undefined && age > ageReq.maximum);

      const fullPassengerData = shopExcursion?.prices?.perPassenger.find(
        (px) => px.id === passenger.id
      );

      const disabled =
        isRestricted ||
        alreadyBookedPaxID?.includes(passenger.id) ||
        (myAreaOBSCardData?.type?.code === 'PARK' && !fullPassengerData?.isApplicable);

      const manuallyDeselected = manuallyDeselectedMap[cabinId]?.[passenger.id];

      if (!allChecked && !disabled) {
        if (isUnderAgeThreshold && hasInfantsUnderThree) {
          updated[passenger.id] = true;
          if (!manuallyDeselected) {
            newAutoSelected[passenger.id] = true;
          } else {
            updated[passenger.id] = false; // Respect manual deselection
          }
        } else {
          updated[passenger.id] = true;
        }
      } else {
        updated[passenger.id] = false;
      }
    });

    if (isAllInclusive) {
      toggleAllPassengers();
      return;
    }

    if (!allChecked) {
      // Clear manual deselect flags only when selecting all (not when unchecking)
      setManualUnderThreeDeselects((prev) => ({ ...prev, [cabinId]: {} }));
    }

    setCheckedPassengers((prev) => ({ ...prev, [cabinId]: updated }));
  }

  /**
   * Toggles the selection state of all passengers across all cabins.
   *
   * This function iterates through all cabins and toggles the selection state
   * of eligible passengers. Eligibility is determined based on the `isApplicable`
   * property from the shop request data. The function ensures that only eligible
   * passengers are toggled, and their current selection state is inverted.
   */
  const toggleAllPassengers = () => {
    const updatedCheckedState: Record<string, Record<string, boolean>> = {};

    Object.entries(checkedPassengers).forEach(([cabinKey, cabinPassengers]) => {
      //Filter just the eligible passengers (eligibility taken from shopRequest) across cabins
      const shopResult = isLayerExcursionOpen ? shopExcursion : shopOBS;
      // create a new variable that contains only the eligiblePassengers.isApplicable === true and the id match with the cabinPassengers keys
      const eligiblePassengersMap = new Map(
        shopResult?.prices?.perPassenger
          ?.filter((passenger) => passenger.isApplicable)
          .map((passenger) => [passenger.id, true])
      );

      updatedCheckedState[cabinKey] = Object.fromEntries(
        Object.keys(cabinPassengers)
          .filter((passengerId) => eligiblePassengersMap.has(passengerId))
          .map((passengerId) => [passengerId, !cabinPassengers[passengerId]])
      );
    });

    setCheckedPassengers(updatedCheckedState);
  };

  function calculatePassengerPrice(priceEntry: ShopOBSPassengerI): {
    afterDiscount: number;
    beforePromotionAndDiscount: number;
  } {
    const total = priceEntry?.total;
    const afterDiscount = Number.parseFloat(
      total?.afterDiscount ?? getAvailablePrice(total) ?? '0'
    );
    const beforePromotionAndDiscount = getBeforePromotionPriceValue(total);

    return {
      afterDiscount: Number.isNaN(afterDiscount) ? 0 : afterDiscount,
      beforePromotionAndDiscount,
    };
  }

  const buildDrawerPrice = (
    afterDiscountSum: number,
    beforePromotionSum: number
  ): ShopExcursionDiscounDetailsI => {
    const afterDiscount = roundPrice(afterDiscountSum.toString()) ?? '';
    const beforePromotionAndDiscount = roundPrice(beforePromotionSum.toString()) ?? '';
    const beforeDiscount =
      beforePromotionAndDiscount !== afterDiscount ? beforePromotionAndDiscount : undefined;

    return { afterDiscount, beforeDiscount, beforePromotionAndDiscount };
  };

  function sumPrices(prices: number[]): number {
    return prices.reduce((total, price) => total + (Number.isFinite(price) ? price : 0), 0);
  }

  function getPriceEntry(priceArray: ShopOBSPassengerI[] | undefined, id: string) {
    return priceArray?.filter((price) => price.isApplicable).find((pax) => pax.id === id);
  }

  // set drawer price with the sum of the selected passengers
  useEffect(() => {
    if (!isDrawerOpen) {
      lastDispatchedDrawerPriceRef.current = '';
      return;
    }

    let card;
    let prices;
    if (isLayerExcursionOpen) {
      card = myAreaExcursionCardData;
      prices = excursionPassengerPrices;
    } else {
      card = myAreaOBSCardData;
      prices = obsPassengerPrices;
    }
    const priceArray = prices.filter((price) => {
      if (!card) {
        return false;
      }

      return (
        price.ageCategory === AgeCategoryEnum.ADULT ||
        price.ageCategory === AgeCategoryEnum.SENIOR ||
        price.ageCategory === AgeCategoryEnum.CHILD ||
        price.ageCategory === AgeCategoryEnum.JUNIOR ||
        price.ageCategory === AgeCategoryEnum.INFANT
      );
    });
    if (!priceArray || priceArray.length === 0) return;

    const afterDiscountSelectedPrices: number[] = [];
    const beforePromotionAndDiscountPrices: number[] = [];

    // Calculate prices for selected passengers
    Object.entries(currentDrawerCheckedPassengers).forEach(([_, cabinPassengers]) => {
      Object.entries(cabinPassengers).forEach(([id, isChecked]) => {
        if (isChecked) {
          const priceEntry = getPriceEntry(priceArray, id);
          if (priceEntry) {
            const { afterDiscount, beforePromotionAndDiscount } =
              calculatePassengerPrice(priceEntry);
            afterDiscountSelectedPrices.push(afterDiscount);
            beforePromotionAndDiscountPrices.push(beforePromotionAndDiscount);
          }
        }
      });
    });

    const afterDiscountSum = sumPrices(afterDiscountSelectedPrices);

    if (afterDiscountSum) {
      const beforePromotionAndDiscountSum = sumPrices(beforePromotionAndDiscountPrices);
      const nextDrawerPrice = buildDrawerPrice(afterDiscountSum, beforePromotionAndDiscountSum);
      const nextDrawerPriceKey = JSON.stringify(nextDrawerPrice);

      if (lastDispatchedDrawerPriceRef.current !== nextDrawerPriceKey) {
        lastDispatchedDrawerPriceRef.current = nextDrawerPriceKey;
        dispatch(drawerPrice(nextDrawerPrice));
      }
    } else {
      // No passenger selected: show the lowest eligible price, but restricted to the
      // logged-in user's active cabin. In multi-cabin scenarios the cheapest applicable
      // passenger may belong to another cabin (e.g. a minor), which would otherwise be
      // shown as the initial "from" price even though it is not in the active cabin.
      const activeCabinPassengerIds = new Set(
        (activeUsersCabin?.passengers ?? [])
          .map((passenger) => passenger?.id)
          .filter((id): id is string => Boolean(id))
      );
      const activeCabinPriceArray =
        activeCabinPassengerIds.size > 0
          ? priceArray.filter((price) => price.id && activeCabinPassengerIds.has(price.id))
          : priceArray;
      const lowestPrice = getLowestApplicablePrice(
        activeCabinPriceArray.length > 0 ? activeCabinPriceArray : priceArray
      );
      const nextDrawerPriceKey = JSON.stringify(lowestPrice);

      if (lastDispatchedDrawerPriceRef.current !== nextDrawerPriceKey) {
        lastDispatchedDrawerPriceRef.current = nextDrawerPriceKey;
        dispatch(drawerPrice(lowestPrice));
      }
    }
  }, [
    isLayerExcursionOpen,
    excursionPassengerPrices,
    obsPassengerPrices,
    currentDrawerCheckedPassengers,
    isDrawerOpen,
    activeUsersCabin,
  ]);

  function isPromoAvailable(
    promoPrice: ShopExcursionDiscounDetailsI | undefined,
    relatedItemCode?: string
  ): boolean {
    let promo;
    if (isLayerExcursionOpen) {
      promo = myAreaExcursionCardData?.promotion;
    } else if (
      relatedItemCode &&
      linkedMinorObsCard?.code &&
      relatedItemCode === linkedMinorObsCard.code
    ) {
      // Minor passenger of a combined adult+minor package: use the minor package's promotion.
      promo = linkedMinorObsCard.promotion;
    } else {
      promo = myAreaOBSCardData?.promotion;
    }
    return isPromotionPriceAvailable(
      promotionPriceEnabled,
      promoPrice,
      promo?.validFrom,
      promo?.validTo
    );
  }

  const loadDiningButtonFunc = () => {
    setIsDiningButtonLoading(true);
    if (diningButtonNext) {
      setTimeout(() => {
        try {
          diningButtonNext();
        } catch (error) {
          setIsDiningButtonLoading(false);
        }
      }, 800);
    }
  };

  const isDiningLayout = () => {
    if (!isDining) {
      return null;
    }

    const shouldShowDiningButton =
      !hideDiningConfirmButton &&
      (reservations.length > 0 || !matchingCartItem) &&
      (hasSelectedPassengers || isDiningButtonLoading);

    return (
      <>
        {showDiningChildDiscountLabel && diningChildDiscountLabel && (
          <div className={styles['passengers__dining-discount-label']}>
            <Text text={diningChildDiscountLabel} />
          </div>
        )}
        {shouldShowDiningButton && (
          <div className={styles['passengers__dining-next']}>
            <Button
              icon={isDiningButtonLoading ? diningConfirmLoadingLabelIcon : undefined}
              text={isDiningButtonLoading ? diningConfirmLoadingLabel : diningConfirmLabel}
              onClick={loadDiningButtonFunc}
              disabled={isDiningButtonLoading}
              styleOption="cmp-button__primary"
            />
          </div>
        )}
      </>
    );
  };

  const renderLongDescription = () => {
    if (hideLongDescription) {
      return null;
    }

    if (isLayerExcursionOpen && !myAreaExcursionCardData?.isPackage) {
      return (
        <Text
          styleOption={styles['passengers__excursionDesc']}
          text={`${extractLongDescription(myAreaExcursionCardData?.longDescription ?? '')}`}
        />
      );
    }
    if (!isLayerExcursionOpen && myAreaOBSCardData?.longDescription) {
      // For an adult ALLINC card with a linked minor package, the minor card is hidden from
      // results, so its description is shown here under the adult's (no merging logic on B2CW —
      // both descriptions come straight from the service).
      const linkedMinorCard = getLinkedMinorObs(myAreaOBSCardData, allOBS, allInclusiveCmsItems);
      const minorLongDescription = linkedMinorCard?.longDescription as
        | { html?: string }
        | string
        | undefined;
      const minorDescriptionText = minorLongDescription
        ? typeof minorLongDescription === 'object'
          ? minorLongDescription.html ?? ''
          : minorLongDescription
        : '';

      return (
        <>
          <Text
            styleOption={styles['passengers__excursionDesc']}
            text={`${extractLongDescription(
              myAreaOBSCardData?.longDescription.html ?? myAreaOBSCardData.longDescription ?? '',
              true
            )}`}
          />
          {minorDescriptionText && (
            <Text
              styleOption={styles['passengers__excursionDesc']}
              text={`${extractLongDescription(minorDescriptionText, true)}`}
            />
          )}
        </>
      );
    }
    return null;
  };

  const hideActiveCabin = hidePassengersInCart && allPassengersInCart;
  const isCurrentCabinTheActiveCabin = !cabinIdFilter || activeUsersCabin?.id === cabinIdFilter;
  const shouldUseGenericPassengersLabel =
    hidePassengersInCart && allPassengersInCart && isCurrentCabinTheActiveCabin;

  const hasEligibleAddOnPassengers = useMemo(() => {
    const ageReq = isLayerExcursionOpen
      ? shopExcursion?.ageRequirements
      : myAreaOBSCardData?.ageRequirements;

    return Object.entries(passengersPerCabin).some(([cabinId, passengers]) => {
      const isActiveCabin = activeUsersCabin?.id === cabinId;
      if (hideActiveCabin && isActiveCabin && isMultiCabin) {
        return false;
      }

      return passengers.some((passenger) => {
        if (!passenger.id) return false;

        const isAlreadyBooked = alreadyBookedPaxID?.includes(passenger.id);
        const isInCart = hidePassengersInCart && cartPassengerIds.has(passenger.id);
        if (isAlreadyBooked || isInCart) return false;

        if (isPassengerRestrictedByItemAgeRequirements(passenger, ageReq)) {
          return false;
        }

        const fullPassengerData = isLayerExcursionOpen
          ? shopExcursion?.prices?.perPassenger.find((px) => px.id === passenger.id)
          : obsPassengerPrices?.find((px) => px.id === passenger.id);

        const isApplicable = fullPassengerData?.isApplicable !== false;
        return isApplicable;
      });
    });
  }, [
    passengersPerCabin,
    activeUsersCabin,
    hideActiveCabin,
    isMultiCabin,
    alreadyBookedPaxID,
    hidePassengersInCart,
    cartPassengerIds,
    shopExcursion?.ageRequirements,
    myAreaOBSCardData?.ageRequirements,
    isLayerExcursionOpen,
    shopExcursion?.prices?.perPassenger,
    obsPassengerPrices,
  ]);

  const hasEligibleAddOnForFilteredCabin = useMemo(() => {
    if (!cabinIdFilter) {
      return true;
    }
    const passengers = passengersPerCabin[cabinIdFilter] ?? [];
    const ageReq = isLayerExcursionOpen
      ? shopExcursion?.ageRequirements
      : myAreaOBSCardData?.ageRequirements;
    const isActiveCabin = activeUsersCabin?.id === cabinIdFilter;
    if (hideActiveCabin && isActiveCabin) {
      return false;
    }
    return passengers.some((passenger) => {
      if (!passenger.id) return false;
      const isAlreadyBooked = alreadyBookedPaxID?.includes(passenger.id);
      const isInCart = hidePassengersInCart && cartPassengerIds.has(passenger.id);
      if (isAlreadyBooked || isInCart) return false;
      if (isPassengerRestrictedByItemAgeRequirements(passenger, ageReq)) {
        return false;
      }
      const fullPassengerData = isLayerExcursionOpen
        ? shopExcursion?.prices?.perPassenger.find((px) => px.id === passenger.id)
        : obsPassengerPrices?.find((px) => px.id === passenger.id);
      const isApplicable = fullPassengerData?.isApplicable !== false;
      const isApplicableForPark =
        !isLayerExcursionOpen &&
        myAreaOBSCardData?.type?.code === 'PARK' &&
        fullPassengerData?.isApplicable === false;
      return isApplicable && !isApplicableForPark;
    });
  }, [
    cabinIdFilter,
    passengersPerCabin,
    hideActiveCabin,
    activeUsersCabin,
    alreadyBookedPaxID,
    hidePassengersInCart,
    cartPassengerIds,
    shopExcursion?.ageRequirements,
    myAreaOBSCardData?.ageRequirements,
    isLayerExcursionOpen,
    shopExcursion?.prices?.perPassenger,
    obsPassengerPrices,
    myAreaOBSCardData?.type?.code,
  ]);

  const isPassengerEligibleForAddonSection = useCallback(
    (passenger: PassengerI): boolean => {
      if (!passenger.id) {
        return false;
      }

      const ageReq = isLayerExcursionOpen
        ? shopExcursion?.ageRequirements
        : myAreaOBSCardData?.ageRequirements;
      if (isPassengerRestrictedByItemAgeRequirements(passenger, ageReq)) {
        return false;
      }

      const fullPassengerData = isLayerExcursionOpen
        ? shopExcursion?.prices?.perPassenger.find((px) => px.id === passenger.id)
        : obsPassengerPrices?.find((px) => px.id === passenger.id);
      const isApplicable = fullPassengerData?.isApplicable !== false;
      const isApplicableForPark =
        !isLayerExcursionOpen &&
        myAreaOBSCardData?.type?.code === 'PARK' &&
        fullPassengerData?.isApplicable === false;

      return isApplicable && !isApplicableForPark;
    },
    [
      isLayerExcursionOpen,
      shopExcursion?.ageRequirements,
      shopExcursion?.prices?.perPassenger,
      myAreaOBSCardData?.ageRequirements,
      myAreaOBSCardData?.type?.code,
      obsPassengerPrices,
    ]
  );

  const getRemainingPassengersForCabin = useCallback(
    (passengers: PassengerI[]) => {
      if (isDining) {
        return passengers;
      }

      return passengers.filter((pax: PassengerI) => {
        if (!pax.id) {
          return false;
        }

        if (
          alreadyBookedPaxID.includes(pax.id) ||
          (hidePassengersInCart && cartPassengerIds.has(pax.id))
        ) {
          return false;
        }

        if (showOnlyAddonEligiblePassengers) {
          return isPassengerEligibleForAddonSection(pax);
        }

        return true;
      });
    },
    [
      isDining,
      alreadyBookedPaxID,
      hidePassengersInCart,
      cartPassengerIds,
      showOnlyAddonEligiblePassengers,
      isPassengerEligibleForAddonSection,
    ]
  );

  const visiblePassengersCount = useMemo(() => {
    const passengerGroups =
      cabinIdFilter && passengersPerCabin[cabinIdFilter]
        ? [passengersPerCabin[cabinIdFilter]]
        : Object.values(passengersPerCabin);

    return passengerGroups.reduce(
      (total, passengers) => total + getRemainingPassengersForCabin(passengers).length,
      0
    );
  }, [cabinIdFilter, passengersPerCabin, getRemainingPassengersForCabin]);

  const getPassengersSectionLabel = useCallback(
    (count: number) => {
      if (
        alreadyBookedPaxID?.length &&
        !isNoEligible &&
        !shouldUseGenericPassengersLabel &&
        !isDining
      ) {
        return drawerAEMConfig.addonEligiblePassengersLabel;
      }

      return managePlurals(count, drawerAEMConfig.passengersLabel);
    },
    [
      alreadyBookedPaxID,
      isNoEligible,
      shouldUseGenericPassengersLabel,
      isDining,
      drawerAEMConfig.addonEligiblePassengersLabel,
      drawerAEMConfig.passengersLabel,
    ]
  );

  /** Passengers not yet booked or in cart — includes ineligible pax that must stay visible (AC9). */
  const hasRemainingVisiblePassengers = useMemo(() => {
    const passengerGroups =
      cabinIdFilter && passengersPerCabin[cabinIdFilter]
        ? [passengersPerCabin[cabinIdFilter]]
        : Object.values(passengersPerCabin);

    return passengerGroups.some((passengers) =>
      passengers.some(
        (passenger) =>
          passenger.id &&
          !alreadyBookedPaxID?.includes(passenger.id) &&
          !(hidePassengersInCart && cartPassengerIds.has(passenger.id))
      )
    );
  }, [
    cabinIdFilter,
    passengersPerCabin,
    alreadyBookedPaxID,
    hidePassengersInCart,
    cartPassengerIds,
  ]);

  const isShopDataReady = isLayerExcursionOpen
    ? Boolean(shopExcursion && myAreaExcursionCardData?.code === shopExcursion?.code)
    : Boolean(shopOBS && myAreaOBSCardData?.code === shopOBS?.code);

  const hasPricingSourceData = isLayerExcursionOpen
    ? Boolean(shopExcursion && myAreaExcursionCardData?.code === shopExcursion?.code)
    : Boolean(shopOBS && myAreaOBSCardData?.code === shopOBS?.code && obsPriceMap);

  useEffect(() => {
    if (!hasPricingSourceData) {
      dispatch(drawerPrice(null));
    }
  }, [dispatch, hasPricingSourceData]);

  if (!isShopDataReady) {
    dispatch(drawerPrice(null));
  }

  if (isPriceTypeNotAllowed(bookingData)) {
    return (
      <div className={clsx(styles['passengers'])}>
        {shouldShowHeadsUpContainer && (
          <HeadsUpContainer
            checkedPassengers={checkedPassengers}
            bookingData={bookingData}
          />
        )}

        {headsUpMessage && (
          <div className={styles['passengers__dining-heads-up']}>
            <HeadsUpInfo
              icon={headsUpMessage.icon}
              title={headsUpMessage.title}
              description={headsUpMessage.body}
              bgColor={headsUpMessage.backgroundColor}
              fontColor={headsUpMessage.contentColor}
              priority={HeadsUpMessagePriority.HIGH}
              visibilityCondition={() => true}
            />
            <hr />
          </div>
        )}
        {renderLongDescription()}
      </div>
    );
  }

  if (!hasPricingSourceData) {
    const skeletonHeading = getPassengersSectionLabel(visiblePassengersCount);

    return (
      //Skeleton
      <div
        className={clsx(styles['passengers'], {
          [styles['passengers__addOnContainer--with-refund']]: withRefundDivider,
        })}
      >
        {showMainTitle &&
          (isMultiCabin && !cabinIdFilter ? (
            <div
              className={clsx(
                styles['passengers__sectionTitle'],
                styles['passengers__sectionTitle--multiCartRoot']
              )}
            >
              <Text text={skeletonHeading} />
            </div>
          ) : (
            <div
              className={
                !isMultiCabin ? styles['passengers__title'] : styles['passengers__sectionTitle']
              }
            >
              <Text text={skeletonHeading} />
            </div>
          ))}
        <PassengersSkeleton />
      </div>
    );
  }

  if (showOnlyAddonEligiblePassengers) {
    if (cabinIdFilter && !hasEligibleAddOnForFilteredCabin) {
      return null;
    }
    if ((!isDining && !hasEligibleAddOnPassengers) || (hideActiveCabin && !isMultiCabin)) {
      return <>{renderLongDescription()}</>;
    }
  } else {
    if (cabinIdFilter && !hasEligibleAddOnForFilteredCabin && !hasRemainingVisiblePassengers) {
      return null;
    }

    if (
      (!isDining && !hasEligibleAddOnPassengers && !hasRemainingVisiblePassengers) ||
      (!isDining && allPassengersInCart && !isMultiCabin) ||
      (hideActiveCabin && !isMultiCabin && !hasRemainingVisiblePassengers)
    ) {
      return <>{renderLongDescription()}</>;
    }
  }

  const addOnSectionLabel = getPassengersSectionLabel(visiblePassengersCount);

  return (
    <div
      className={clsx(styles['passengers'], {
        [styles['passengers__addOnContainer--with-refund']]: withRefundDivider,
      })}
    >
      {isMultiCabin && showMainTitle && !cabinIdFilter && (
        <div
          className={clsx(
            styles['passengers__sectionTitle'],
            styles['passengers__sectionTitle--multiCartRoot']
          )}
        >
          <Text text={addOnSectionLabel} />
        </div>
      )}

      {!isMultiCabin && showMainTitle && (
        <div className={clsx(styles['passengers__title'], { loading: isDiningButtonLoading })}>
          <Text text={addOnSectionLabel} />
        </div>
      )}

      {shouldShowHeadsUpContainer && (
        <HeadsUpContainer
          checkedPassengers={checkedPassengers}
          bookingData={bookingData}
        />
      )}

      {headsUpMessage && (
        <div className={styles['passengers__dining-heads-up']}>
          <HeadsUpInfo
            icon={headsUpMessage.icon}
            title={headsUpMessage.title}
            description={headsUpMessage.body}
            bgColor={headsUpMessage.backgroundColor}
            fontColor={headsUpMessage.contentColor}
            priority={HeadsUpMessagePriority.HIGH}
            visibilityCondition={() => true}
          />
          <hr />
        </div>
      )}

      {Object.entries(passengersPerCabin).map(([cabinId, passengers], cabinIndex) => {
        if (cabinIdFilter && cabinId !== cabinIdFilter) {
          return null;
        }
        const disableCabin = activeUsersCabin ? activeUsersCabin.id !== cabinId : false;

        const ageReq = isLayerExcursionOpen
          ? shopExcursion?.ageRequirements
          : myAreaOBSCardData?.ageRequirements;

        const isAnyEligiblePassenger = passengers.some((passenger) => {
          if (!passenger.id) return false;

          const isAlreadyBooked = alreadyBookedPaxID?.includes(passenger.id);
          const isInCart = hidePassengersInCart && cartPassengerIds.has(passenger.id);

          const fullPassengerData = isLayerExcursionOpen
            ? shopExcursion?.prices?.perPassenger.find((px) => px.id === passenger.id)
            : obsPassengerPrices?.find((px) => px.id === passenger.id);
          const isApplicableForPark = parkOBSType && !fullPassengerData?.isApplicable;

          // Passenger eligible if not restricted, not booked, and (if PARK type, is applicable)
          return (
            !isPassengerRestrictedByItemAgeRequirements(passenger, ageReq) &&
            !isAlreadyBooked &&
            !isApplicableForPark &&
            !isInCart
          );
        });

        const isActiveCabin = activeUsersCabin?.id === cabinId;
        const showSelectAllRow =
          !hideSelectAllCheckbox &&
          !isDisabledCompletedDiningItem &&
          myAreaOBSCardData?.type?.code !== 'PARK' &&
          !isNoEligible &&
          isActiveCabin;

        const remainingPassengers = getRemainingPassengersForCabin(passengers);

        // check of all passengers which passengers are elligible to be added to the cart
        const eligibleCabinPassengers = passengers.filter((passenger) => {
          if (!passenger.id) return false;
          if (alreadyBookedPaxID?.includes(passenger.id)) return false;
          const fullPassengerData = isLayerExcursionOpen
            ? shopExcursion?.prices?.perPassenger.find((px) => px.id === passenger.id)
            : obsPassengerPrices?.find((px) => px.id === passenger.id);
          const isApplicable = fullPassengerData?.isApplicable !== false;
          const isRestricted = isPassengerRestrictedByItemAgeRequirements(passenger, ageReq);
          return isApplicable && !isRestricted;
        });

        // if we have all elligible passengers
        // hide the whole cabin if is a multicabin
        // and all elligible are to the cart
        // this will be true only in multi-cabin scenario
        const allMultiCabinPassengersInCart =
          isActiveCabin &&
          isMultiCabin &&
          hidePassengersInCart &&
          eligibleCabinPassengers.length > 0 &&
          eligibleCabinPassengers.every((passenger) => cartPassengerIds.has(passenger.id));

        const passengersToRender = showOnlyAddonEligiblePassengers
          ? remainingPassengers
          : passengers;

        return remainingPassengers.length > 0 && !allMultiCabinPassengersInCart ? (
          <div
            key={cabinId}
            className={`${styles['passengers__cabinGroup']} ${
              isMultiCabin ? styles['multi-cabin'] : ''
            }`}
          >
            {showCabinTitle && (
              <div className={styles['passengers__cabinGroup__title']}>
                <Text
                  text={replacePlaceholders(drawerAEMConfig.stateroomLabel, {
                    id: (cabinIndex + 1).toString(),
                  })}
                />
              </div>
            )}
            {showPerCabinPassengersHeading && showMainTitle && (
              <div className={styles['passengers__sectionTitle']}>
                <Text text={getPassengersSectionLabel(remainingPassengers.length)} />
              </div>
            )}
            {/* All passenger should not render for OBS PARK type */}
            {showSelectAllRow ? (
              <>
                <Checkbox
                  styleOptions={styles['passengers__checkboxes__all']}
                  checked={isAllCabinChecked(cabinId)}
                  onChange={() => handleSelectAllCabin(cabinId)}
                  text={
                    <Text
                      text={
                        alreadyBookedPaxID?.length
                          ? drawerAEMConfig.allEligiblePassengersLabel
                          : drawerAEMConfig.allPassengersLabel
                      }
                    />
                  }
                  disabled={disableCabin || !isAnyEligiblePassenger || isDiningButtonLoading}
                />
                {!(isMultiCabin && isAllInclusive) && <hr />}
              </>
            ) : null}
            <div className={styles['passengers__checkboxes__container']}>
              {passengersToRender.map((passenger, index) => {
                const isRestrictedLangSelectionPassenger =
                  isRestrictedLanguageSelection && myAreaExcursionCardData
                    ? cartItems
                        .find(
                          (item: CartItem) =>
                            item.code === myAreaExcursionCardData.code &&
                            item.startDate === myAreaExcursionCardData.startDate
                        )
                        ?.passengers.find((cartPassenger) => passenger.id === cartPassenger.id)
                    : false;
                const manualDeselected = manualUnderThreeDeselects?.[cabinId]?.[passenger.id];
                const checked = manualDeselected
                  ? false
                  : !isActiveCabin && inactiveCabinsBookedPaxIds.includes(passenger.id)
                  ? true
                  : checkedPassengers[cabinId]?.[passenger.id] || false;

                const passengerPriceData: ShopExcursionDiscounDetailsI | undefined =
                  isLayerExcursionOpen
                    ? excursionPriceMap?.get(passenger.number)
                    : obsPriceMap?.get(passenger.number);

                const fullPassengerData = shopExcursion?.prices?.perPassenger.find(
                  (px) => px.id === passenger.id
                );

                const ageRequirements = isLayerExcursionOpen
                  ? shopExcursion?.ageRequirements
                  : myAreaOBSCardData?.ageRequirements;

                const isRestricted = isPassengerRestrictedByItemAgeRequirements(
                  passenger,
                  ageRequirements
                );

                const obsFullPassengerData = obsPassengerPrices?.find(
                  (px) => px.id === passenger.id
                );
                const excursionFullPassengerData = shopExcursion?.prices?.perPassenger.find(
                  (px) => px.id === passenger.id
                );

                const isApplicable = isLayerExcursionOpen
                  ? excursionFullPassengerData?.isApplicable
                  : obsFullPassengerData?.isApplicable;

                const hidden =
                  !showOnlyAddonEligiblePassengers &&
                  !isDining &&
                  (alreadyBookedPaxID?.includes(passenger.id) ||
                    (hidePassengersInCart && cartPassengerIds.has(passenger.id)));

                const disabled =
                  isDisabledCompletedDiningItem ||
                  (!showOnlyAddonEligiblePassengers && isRestricted) ||
                  (!showOnlyAddonEligiblePassengers &&
                    alreadyBookedPaxID?.includes(passenger.id)) ||
                  (!showOnlyAddonEligiblePassengers &&
                    hidePassengersInCart &&
                    cartPassengerIds.has(passenger.id)) ||
                  disableCabin ||
                  (!showOnlyAddonEligiblePassengers && isApplicable === false) ||
                  isDiningButtonLoading;

                const languageDropdownDisabled =
                  alreadyBookedPaxID?.includes(passenger.id) ||
                  isRestrictedLangSelectionPassenger ||
                  !checked ||
                  disableCabin ||
                  (parkOBSType && !fullPassengerData?.isApplicable) ||
                  isRestricted;

                const passengerBookedExcursion =
                  myAreaExcursionCardData &&
                  bookingData?.prepaid?.bookings
                    ?.flatMap((booking) => booking.excursions ?? [])
                    .find(
                      (excursion) =>
                        excursion.code === myAreaExcursionCardData.code &&
                        excursion?.passenger?.id === passenger?.id &&
                        (!myAreaExcursionCardData.startDate ||
                          excursion.startDate === myAreaExcursionCardData.startDate)
                    );

                return (
                  <div
                    key={passenger.id}
                    className={styles['passengers__checkboxes__pax']}
                    hidden={hidden}
                  >
                    <div className={styles['passengers__checkboxes__singleBox']}>
                      <Checkbox
                        checked={checked || alreadyBookedPaxID?.includes(passenger.id)}
                        onChange={() => handleCabinCheckboxChange(cabinId, passenger)}
                        disabled={disabled}
                        text={
                          <>
                            <Text text={`${passenger.name} ${passenger.surname}`} />
                            {!isPerCabinPriceBasis &&
                              !disabled &&
                              getAvailablePrice(passengerPriceData) && (
                                <Price
                                  additionalText={
                                    diningDiscountedMinorIds.has(passenger.id) &&
                                    diningChildDiscountLabel
                                      ? ` ${diningChildDiscountIndicatorLabel}`
                                      : undefined
                                  }
                                  passengerPrice={passengerPriceData}
                                  isPromotionAvailable={isPromoAvailable(
                                    passengerPriceData,
                                    obsFullPassengerData?.relatedItemCode
                                  )}
                                />
                              )}
                          </>
                        }
                      />
                    </div>

                    {isLayerExcursionOpen &&
                      languageList &&
                      languageList.length > 1 &&
                      isActiveCabin && (
                        <div className={styles['passengers__checkboxes__dropdown']}>
                          <Controller
                            control={control}
                            name={`passengers.${passenger.id}.language`}
                            defaultValue={
                              getLanguageLabelFromCode(
                                passengerBookedExcursion?.preferredGuideLanguage
                              ) || ''
                            }
                            rules={{ required: true }}
                            render={({ field }) => (
                              <LanguageSelect
                                passengerId={passenger.id}
                                selectedLanguages={selectedLanguages}
                                setSelectedLanguages={setSelectedLanguages}
                                setLastSelectedLanguage={setLastSelectedLanguage}
                                label=""
                                placeholder={drawerExcursionAEMConfig?.selectLanguageLabel ?? ''}
                                ariaLabel={drawerExcursionAEMConfig?.selectLanguageLabel ?? ''}
                                items={languageList.map((lang) => ({
                                  value: lang,
                                  text: getLanguageLabelFromCode(lang),
                                }))}
                                fieldName={field.name}
                                value={field.value || selectedLanguages?.[passenger.id]}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                errors={
                                  (errors.passengers as FieldErrors[] | undefined)?.[index]
                                    ?.language
                                }
                                clearErrors={clearErrors}
                                disabled={languageDropdownDisabled}
                              />
                            )}
                          />
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;
      })}
      {isDiningLayout()}
      {beforeLongDescription}
      {renderLongDescription()}
    </div>
  );
}

export default Passengers;
